import { Address, formatEther, formatUnits } from 'viem';
import { getPublicClient } from 'wagmi/actions';
import { config } from '../config';
import { ERC20_ABI } from '../hooks/useContractInteraction';

/**
 * Get native token balance (ETH, BNB, MATIC, etc.)
 */
export async function getNativeBalance(
  address: Address,
  chainId?: number
): Promise<bigint> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const balance = await client.getBalance({ address });
    return balance;
  } catch (error) {
    console.error('Failed to get native balance:', error);
    throw error;
  }
}

/**
 * Get native token balance formatted as string
 */
export async function getNativeBalanceFormatted(
  address: Address,
  chainId?: number,
  decimals: number = 4
): Promise<string> {
  const balance = await getNativeBalance(address, chainId);
  const formatted = formatEther(balance);
  return parseFloat(formatted).toFixed(decimals);
}

/**
 * Get ERC20 token balance
 */
export async function getTokenBalance(
  tokenAddress: Address,
  userAddress: Address,
  chainId?: number
): Promise<bigint> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const balance = await client.readContract({
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: 'balanceOf',
      args: [userAddress],
    });

    return balance as bigint;
  } catch (error) {
    console.error('Failed to get token balance:', error);
    throw error;
  }
}

/**
 * Get ERC20 token balance formatted
 */
export async function getTokenBalanceFormatted(
  tokenAddress: Address,
  userAddress: Address,
  decimals: number = 18,
  displayDecimals: number = 4,
  chainId?: number
): Promise<string> {
  const balance = await getTokenBalance(tokenAddress, userAddress, chainId);
  const formatted = formatUnits(balance, decimals);
  return parseFloat(formatted).toFixed(displayDecimals);
}

/**
 * Get ERC20 token info (name, symbol, decimals)
 */
export async function getTokenInfo(
  tokenAddress: Address,
  chainId?: number
): Promise<{
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: bigint;
}> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const [name, symbol, decimals, totalSupply] = await Promise.all([
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'name',
      }) as Promise<string>,
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'symbol',
      }) as Promise<string>,
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'decimals',
      }) as Promise<number>,
      client.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'totalSupply',
      }) as Promise<bigint>,
    ]);

    return { name, symbol, decimals, totalSupply };
  } catch (error) {
    console.error('Failed to get token info:', error);
    throw error;
  }
}

/**
 * Get current gas price
 */
export async function getGasPrice(chainId?: number): Promise<bigint> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const gasPrice = await client.getGasPrice();
    return gasPrice;
  } catch (error) {
    console.error('Failed to get gas price:', error);
    throw error;
  }
}

/**
 * Get current block number
 */
export async function getBlockNumber(chainId?: number): Promise<bigint> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const blockNumber = await client.getBlockNumber();
    return blockNumber;
  } catch (error) {
    console.error('Failed to get block number:', error);
    throw error;
  }
}

/**
 * Get transaction receipt
 */
export async function getTransactionReceipt(txHash: `0x${string}`, chainId?: number) {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const receipt = await client.getTransactionReceipt({
      hash: txHash,
    });

    return receipt;
  } catch (error) {
    console.error('Failed to get transaction receipt:', error);
    throw error;
  }
}

/**
 * Wait for transaction confirmation
 */
export async function waitForTransaction(
  txHash: `0x${string}`,
  confirmations: number = 1,
  chainId?: number
) {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const receipt = await client.waitForTransactionReceipt({
      hash: txHash,
      confirmations,
    });

    return receipt;
  } catch (error) {
    console.error('Failed to wait for transaction:', error);
    throw error;
  }
}

/**
 * Check if address is a contract
 */
export async function isContract(address: Address, chainId?: number): Promise<boolean> {
  try {
    const client = getPublicClient(config, chainId ? { chainId: chainId as 1 | 56 | 137 | 97 | 11155111 } : undefined);

    if (!client) {
      throw new Error('Failed to get public client');
    }

    const code = await client.getBytecode({ address });
    return !!code && code !== '0x';
  } catch (error) {
    console.error('Failed to check if address is contract:', error);
    return false;
  }
}

/**
 * Format address for display (0x1234...5678)
 */
export function formatAddress(address: string, prefixLength: number = 6, suffixLength: number = 4): string {
  if (!address) return '';
  if (address.length <= prefixLength + suffixLength) return address;
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * Validate Ethereum address
 */
export function isValidAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

/**
 * Convert hex to decimal
 */
export function hexToDecimal(hex: string): number {
  return parseInt(hex, 16);
}

/**
 * Convert decimal to hex
 */
export function decimalToHex(decimal: number): string {
  return `0x${decimal.toString(16)}`;
}

/**
 * Sleep utility for polling
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry utility for failed requests
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  delayMs: number = 1000
): Promise<T> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxAttempts) throw error;
      await sleep(delayMs * attempt);
    }
  }
  throw new Error('Retry failed');
}
